import { useRef } from "react";
import { freshCells, type Rgb } from "./flag";

export interface FlagBoardProps {
  w: number;
  h: number;
  palette: Rgb[];
  /** Palette digits, "-" = hidden; the full flag once the game is over. */
  revealed: string;
  status: "playing" | "won" | "lost";
}

export const isLight = () => document.documentElement.dataset.scheme === "light";

/**
 * Per-cell animation clock shared by the boards: when `revealed` gains cells,
 * each fresh cell gets a start time (`order` decides the sequence, spread over
 * `spanMs`). 0 = at rest. Returns the clock and whether anything was scheduled.
 */
export function useCellClock(revealed: string, order: (fresh: number[]) => number[], spanMs: number) {
  const starts = useRef<Float64Array | null>(null);
  const prev = useRef<string | null>(null);
  let scheduled = false;
  if (starts.current === null || starts.current.length !== revealed.length) {
    starts.current = new Float64Array(revealed.length);
  }
  if (prev.current !== revealed) {
    const fresh = freshCells(prev.current, revealed);
    if (fresh.length > 0) {
      const seq = order(fresh);
      const now = performance.now();
      const step = seq.length > 1 ? spanMs / (seq.length - 1) : 0;
      seq.forEach((cell, rank) => {
        (starts.current as Float64Array)[cell] = now + rank * step;
      });
      scheduled = true;
    }
    prev.current = revealed;
  }
  return { starts: starts.current, scheduled };
}
